import React, { useState } from 'react';
import { Phone, Mail, MapPin, Instagram, Send, Clock, CheckCircle } from 'lucide-react';
import { contactInfo } from '../mockData';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/sonner';
import { toast } from 'sonner';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Simulate form submission
    setTimeout(() => {
      toast.success('Message envoyé !', {
        description: 'Nous vous contacterons très bientôt.',
      });
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        message: ''
      });
      setIsSubmitting(false);
    }, 1500);
  };

  return (
    <div className="contact-page">
      <Toaster position="top-right" richColors />
      
      {/* Hero Section */}
      <section className="contact-hero">
        <div className="container">
          <div className="contact-hero-content fade-in">
            <h1 className="page-title">
              <span className="text-yellow">Contactez</span><br />
              NDV MOTORS
            </h1>
            <p className="page-subtitle">
              Une question ? Un projet ? N'hésitez pas à nous contacter. Notre équipe est à votre disposition pour vous conseiller et établir un devis gratuit.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="contact-info-section section-padding">
        <div className="container">
          <div className="contact-info-grid">
            <div className="contact-info-card fade-in">
              <div className="contact-info-icon">
                <Phone size={32} />
              </div>
              <h3 className="contact-info-title">Téléphone</h3>
              <a href={`tel:${contactInfo.phone.replace(/\s/g, '')}`} className="contact-info-value">
                {contactInfo.phone}
              </a>
              <p className="contact-info-desc">Lundi - Vendredi : 8h - 18h</p>
            </div>
            <div className="contact-info-card fade-in" style={{ animationDelay: '0.1s' }}>
              <div className="contact-info-icon">
                <Mail size={32} />
              </div>
              <h3 className="contact-info-title">Email</h3>
              <a href={`mailto:${contactInfo.email}`} className="contact-info-value">
                {contactInfo.email}
              </a>
              <p className="contact-info-desc">Réponse sous 24h</p>
            </div>
            <div className="contact-info-card fade-in" style={{ animationDelay: '0.2s' }}>
              <div className="contact-info-icon">
                <MapPin size={32} />
              </div>
              <h3 className="contact-info-title">Adresse</h3>
              <p className="contact-info-value">
                {contactInfo.address}<br />
                {contactInfo.city}
              </p>
              <p className="contact-info-desc">Parking disponible</p>
            </div>
            <div className="contact-info-card fade-in" style={{ animationDelay: '0.3s' }}>
              <div className="contact-info-icon">
                <Instagram size={32} />
              </div>
              <h3 className="contact-info-title">Instagram</h3>
              <a
                href={contactInfo.instagramUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="contact-info-value"
              >
                {contactInfo.instagram}
              </a>
              <p className="contact-info-desc">Suivez nos réalisations</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="contact-form-section section-padding">
        <div className="container">
          <div className="contact-form-grid">
            <div className="form-intro slide-in-left">
              <h2 className="section-title">
                Demandez Votre <span className="text-yellow">Devis Gratuit</span>
              </h2>
              <p className="form-intro-text">
                Remplissez le formulaire ci-contre et nous vous répondrons dans les plus brefs délais avec un devis personnalisé.
              </p>
              <div className="form-benefits">
                <div className="form-benefit">
                  <CheckCircle size={24} className="benefit-icon" />
                  <span>Devis gratuit et sans engagement</span>
                </div>
                <div className="form-benefit">
                  <CheckCircle size={24} className="benefit-icon" />
                  <span>Réponse rapide sous 24h</span>
                </div>
                <div className="form-benefit">
                  <CheckCircle size={24} className="benefit-icon" />
                  <span>Conseils personnalisés</span>
                </div>
                <div className="form-benefit">
                  <CheckCircle size={24} className="benefit-icon" />
                  <span>Service de qualité garantie</span>
                </div>
              </div>
              <div className="contact-hours">
                <Clock size={24} className="hours-icon" />
                <div>
                  <h4 className="hours-title">Horaires d'ouverture</h4>
                  <p className="hours-text">{contactInfo.hours.weekdays}</p>
                  <p className="hours-text">{contactInfo.hours.saturday}</p>
                  <p className="hours-text hours-closed">{contactInfo.hours.sunday}</p>
                </div>
              </div>
            </div>
            <div className="form-container slide-in-right">
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Nom complet *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="Votre nom"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="votre@email.com"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Téléphone *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className="form-input"
                    required
                    placeholder="06 12 34 56 78"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="service" className="form-label">Service souhaité *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className="form-select"
                    required
                  >
                    <option value="">Sélectionnez un service</option>
                    <option value="carrosserie">Carrosserie Automobile</option>
                    <option value="peinture">Peinture Automobile</option>
                    <option value="sinistre">Réparation Après Sinistre</option>
                    <option value="jantes">Jantes Haut de Gamme</option>
                    <option value="renovation">Rénovation Esthétique</option>
                    <option value="autre">Autre / Conseil</option>
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    className="form-textarea"
                    required
                    rows="6"
                    placeholder="Décrivez votre projet ou votre demande..."
                  ></textarea>
                </div>
                <button type="submit" className="btn-primary form-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Envoi en cours...' : 'Envoyer la demande'}
                  <Send size={20} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Map Section */}
      <section className="map-section">
        <div className="map-container">
          <iframe
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2607.8!2d6.1917!3d49.3558!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zNDnCsDIxJzIwLjkiTiA2wrAxMScyOS45IkU!5e0!3m2!1sfr!2sfr!4v1234567890123!5m2!1sfr!2sfr"
            width="100%"
            height="450"
            style={{ border: 0 }}
            allowFullScreen=""
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            title="NDV Motors Location"
          ></iframe>
        </div>
      </section>

      <style jsx>{`
        /* Hero Section */
        .contact-hero {
          background: linear-gradient(135deg, var(--bg-page) 0%, var(--bg-card) 100%);
          padding: 160px 40px 80px;
          border-bottom: 3px solid var(--brand-yellow);
        }

        .contact-hero-content {
          max-width: 900px;
          margin: 0 auto;
          text-align: center;
        }

        .page-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2.5rem, 6vw, 5rem);
          line-height: 1.1;
          color: var(--text-primary);
          margin-bottom: 24px;
        }

        .page-subtitle {
          font-size: clamp(1rem, 2vw, 1.3rem);
          line-height: 1.7;
          color: var(--text-secondary);
          max-width: 700px;
          margin: 0 auto;
        }

        /* Contact Info Cards */
        .contact-info-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 24px;
        }

        .contact-info-card {
          background: var(--bg-card);
          padding: 32px 24px;
          border-radius: var(--radius-lg);
          border: 2px solid var(--border-light);
          text-align: center;
          transition: all 0.3s ease;
        }

        .contact-info-card:hover {
          border-color: var(--brand-yellow);
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(255, 193, 7, 0.2);
        }

        .contact-info-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 80px;
          height: 80px;
          margin: 0 auto 20px;
          background: linear-gradient(135deg, var(--brand-yellow) 0%, var(--brand-yellow-dark) 100%);
          color: var(--brand-black);
          border-radius: 50%;
        }

        .contact-info-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.2rem;
          color: var(--brand-yellow);
          margin-bottom: 12px;
        }

        .contact-info-value {
          font-size: 1.1rem;
          font-weight: 600;
          color: var(--text-primary);
          margin-bottom: 8px;
          display: block;
          text-decoration: none;
          transition: color 0.3s ease;
        }

        .contact-info-value:hover {
          color: var(--brand-yellow);
        }

        .contact-info-desc {
          font-size: 0.9rem;
          color: var(--text-secondary);
          margin: 0;
        }

        /* Form Section */
        .contact-form-section {
          background: var(--bg-page);
        }

        .contact-form-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(400px, 1fr));
          gap: 60px;
          align-items: start;
        }

        .section-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 900;
          font-size: clamp(2rem, 4vw, 3rem);
          line-height: 1.2;
          color: var(--text-primary);
          margin-bottom: 24px;
        }

        .form-intro-text {
          font-size: 1.1rem;
          line-height: 1.7;
          color: var(--text-secondary);
          margin-bottom: 32px;
        }

        .form-benefits {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 32px;
        }

        .form-benefit {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1rem;
          color: var(--text-primary);
        }

        .benefit-icon {
          color: var(--brand-yellow);
          flex-shrink: 0;
        }

        .contact-hours {
          display: flex;
          gap: 16px;
          padding: 24px;
          background: var(--bg-card);
          border-radius: var(--radius-lg);
          border: 2px solid var(--border-light);
        }

        .hours-icon {
          color: var(--brand-yellow);
          flex-shrink: 0;
        }

        .hours-title {
          font-family: 'Montserrat', sans-serif;
          font-weight: 700;
          font-size: 1.2rem;
          color: var(--brand-yellow);
          margin-bottom: 8px;
        }

        .hours-text {
          font-size: 0.95rem;
          color: var(--text-secondary);
          margin: 4px 0;
        }

        .hours-closed {
          color: var(--text-secondary);
          opacity: 0.7;
        }

        .form-container {
          background: var(--bg-card);
          padding: 48px;
          border-radius: var(--radius-lg);
          border: 2px solid var(--border-light);
        }

        .contact-form {
          display: flex;
          flex-direction: column;
          gap: 24px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .form-label {
          font-weight: 600;
          font-size: 0.95rem;
          color: var(--text-primary);
        }

        .form-input,
        .form-select,
        .form-textarea {
          background: var(--bg-page);
          border: 2px solid var(--border-light);
          border-radius: var(--radius-md);
          padding: 14px 16px;
          font-family: 'Inter', sans-serif;
          font-size: 1rem;
          color: var(--text-primary);
          transition: all 0.3s ease;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          outline: none;
          border-color: var(--brand-yellow);
          box-shadow: 0 0 0 3px rgba(255, 193, 7, 0.1);
        }

        .form-textarea {
          resize: vertical;
          min-height: 120px;
        }

        .form-submit {
          width: 100%;
          margin-top: 8px;
        }

        /* Map Section */
        .map-section {
          margin-top: 0;
        }

        .map-container {
          width: 100%;
          height: 450px;
          border-top: 3px solid var(--brand-yellow);
        }

        .map-container iframe {
          filter: grayscale(20%) contrast(1.1);
        }

        /* Responsive */
        @media (max-width: 767px) {
          .contact-hero {
            padding: 120px 20px 60px;
          }

          .contact-info-grid {
            grid-template-columns: 1fr;
          }

          .contact-form-grid {
            grid-template-columns: 1fr;
            gap: 40px;
          }

          .form-container {
            padding: 32px 24px;
          }

          .map-container {
            height: 350px;
          }
        }
      `}</style>
    </div>
  );
};

export default Contact;
